import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  ChevronDown, ChevronUp, FileText, CheckCircle, XCircle, Clock, 
  Download, Trash2, Edit, Eye 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DetailedStageView = ({ stage, stageIndex, studentId, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [consultations, setConsultations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [offerLetters, setOfferLetters] = useState([]);
  const [medicalAppointment, setMedicalAppointment] = useState(null);
  const [financialInfo, setFinancialInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingNotes, setEditingNotes] = useState(stage.notes || '');

  useEffect(() => {
    if (expanded) {
      fetchStageData();
    }
  }, [expanded, stageIndex]);

  const fetchStageData = async () => {
    setLoading(true);
    try {
      if (stageIndex === 0) {
        // Consultation
        const res = await axios.get(`${API_URL}/api/consultation/list`);
        setConsultations(res.data.filter(c => c.student_id === studentId));
      } else if (stageIndex === 1) {
        // Documents
        const res = await axios.get(`${API_URL}/api/documents/detailed`);
        setDocuments(res.data.filter(d => d.student_id === studentId));
      } else if (stageIndex === 2) {
        // Universities
        const res = await axios.get(`${API_URL}/api/universities/shortlist?student_id=${studentId}`);
        setUniversities(res.data);
      } else if (stageIndex === 3) {
        // Offer Letters
        const res = await axios.get(`${API_URL}/api/offer-letters?student_id=${studentId}`);
        setOfferLetters(res.data);
      } else if (stageIndex === 4) {
        // Deposit Payment
        const res = await axios.get(`${API_URL}/api/documents`);
        setDocuments(res.data.filter(d => d.student_id === studentId && d.stage_index === 4));
      } else if (stageIndex === 5) {
        // Medical
        const res = await axios.get(`${API_URL}/api/medical/appointment?student_id=${studentId}`);
        setMedicalAppointment(res.data);
      } else if (stageIndex === 6) {
        // Financial
        const res = await axios.get(`${API_URL}/api/financial/info?student_id=${studentId}`);
        setFinancialInfo(res.data);
      } else if (stageIndex === 7) {
        // Interview Prep
        const res = await axios.get(`${API_URL}/api/preparation/materials`);
        setDocuments(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch stage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStageStatus = async (newStatus) => {
    try {
      await axios.put(`${API_URL}/api/consultant/students/stage`, {
        student_id: studentId,
        stage_index: stageIndex,
        status: newStatus,
        notes: editingNotes
      });
      toast.success('Stage status updated');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const saveNotes = async () => {
    try {
      await axios.put(`${API_URL}/api/consultant/students/stage`, {
        student_id: studentId,
        stage_index: stageIndex,
        status: stage.status,
        notes: editingNotes
      });
      toast.success('Notes saved');
      onRefresh();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const updateConsultationStatus = async (consultationId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/consultation/${consultationId}/status?new_status=${newStatus}`);
      toast.success('Consultation updated');
      fetchStageData();
    } catch (error) {
      toast.error('Failed to update consultation');
    }
  };

  const updateDocumentStatus = async (docId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/documents/detailed/${docId}/status?new_status=${newStatus}`);
      toast.success('Document status updated');
      fetchStageData();
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const deleteUniversity = async (uniId) => {
    try {
      await axios.delete(`${API_URL}/api/universities/${uniId}`);
      toast.success('University removed');
      fetchStageData();
    } catch (error) {
      toast.error('Failed to delete university');
    }
  };

  const deleteOfferLetter = async (offerId) => {
    try {
      await axios.delete(`${API_URL}/api/offer-letters/${offerId}`);
      toast.success('Offer letter deleted');
      fetchStageData();
    } catch (error) {
      toast.error('Failed to delete offer letter');
    }
  };

  return (
    <Card className={`border-2 ${
      stage.status === 'completed' ? 'border-success bg-success/5' :
      'border-border'
    }`}>
      <CardHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading font-semibold text-lg flex items-center gap-2">
              {stageIndex + 1}. {stage.name}
              <Badge variant={
                stage.status === 'completed' ? 'success' :
                stage.status === 'in_progress' ? 'warning' :
                'secondary'
              }>
                {stage.status.replace('_', ' ')}
              </Badge>
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              {expanded ? 'Hide' : 'View'} Details
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={stage.status} onValueChange={updateStageStatus}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <div className="flex gap-1">
                <Input
                  placeholder="Add notes..."
                  value={editingNotes}
                  onChange={(e) => setEditingNotes(e.target.value)}
                  className="h-9"
                />
                <Button size="sm" onClick={saveNotes} className="h-9">
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="border-t pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Stage 0: Consultation */}
              {stageIndex === 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Consultation Requests ({consultations.length})</h4>
                  {consultations.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No consultations scheduled</p>
                  ) : (
                    consultations.map(consultation => (
                      <div key={consultation.id} className="p-3 rounded-lg border bg-card">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">{consultation.scheduled_date} at {consultation.scheduled_time}</p>
                            <p className="text-xs text-muted-foreground mt-1">{consultation.description}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={consultation.status === 'confirmed' ? 'success' : 'warning'}>
                              {consultation.status}
                            </Badge>
                            {consultation.status === 'pending' && (
                              <Button size="sm" onClick={() => updateConsultationStatus(consultation.id, 'confirmed')}>
                                Confirm
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Stage 1: Documents */}
              {stageIndex === 1 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Submitted Documents ({documents.length})</h4>
                  {documents.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No documents uploaded yet</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {documents.map(doc => (
                        <div key={doc.id} className="p-3 rounded-lg border bg-card">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <FileText className="h-4 w-4 text-primary" />
                                <p className="font-medium text-sm">{doc.document_name}</p>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                              </p>
                              <div className="flex gap-1 mt-2">
                                <Button
                                  size="sm"
                                  variant={doc.status === 'verified' ? 'success' : 'outline'}
                                  onClick={() => updateDocumentStatus(doc.id, 'verified')}
                                  className="h-7 text-xs"
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" /> Verify
                                </Button>
                                <Button
                                  size="sm"
                                  variant={doc.status === 'rejected' ? 'destructive' : 'outline'}
                                  onClick={() => updateDocumentStatus(doc.id, 'rejected')}
                                  className="h-7 text-xs"
                                >
                                  <XCircle className="h-3 w-3 mr-1" /> Reject
                                </Button>
                              </div>
                            </div>
                            <a href={doc.file_data} download className="text-primary">
                              <Download className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Stage 2: Universities */}
              {stageIndex === 2 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Shortlisted Universities ({universities.length}/5)</h4>
                  {universities.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No universities shortlisted</p>
                  ) : (
                    universities.map((uni, idx) => (
                      <div key={uni.id} className="p-3 rounded-lg border bg-card">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-semibold text-sm text-primary">{idx + 1}. {uni.university_name}</p>
                            <p className="text-sm text-foreground mt-1">{uni.course_title}</p>
                            <p className="text-xs text-muted-foreground mt-1">{uni.country}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteUniversity(uni.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Stage 3: Offer Letters */}
              {stageIndex === 3 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Offer Letters ({offerLetters.length}/5)</h4>
                  {offerLetters.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No offer letters uploaded</p>
                  ) : (
                    offerLetters.map(offer => (
                      <div key={offer.id} className="p-4 rounded-lg border bg-card">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold text-foreground">{offer.university_name}</p>
                            <p className="text-sm text-muted-foreground">{offer.course_title}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge>{offer.status}</Badge>
                            <Button size="sm" variant="ghost" onClick={() => deleteOfferLetter(offer.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">Country</p>
                            <p className="font-medium">{offer.country}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Tuition Fee</p>
                            <p className="font-medium">{offer.tuition_fee}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Scholarship</p>
                            <p className="font-medium text-success">{offer.scholarship_amount}</p>
                          </div>
                        </div>
                        <a href={offer.offer_letter_url} download className="text-xs text-primary flex items-center gap-1 mt-2">
                          <Download className="h-3 w-3" /> Download Offer Letter
                        </a>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Stage 4: Deposit Payment */}
              {stageIndex === 4 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Deposit Payment Receipt</h4>
                  {documents.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No deposit receipt uploaded</p>
                  ) : (
                    documents.map(doc => (
                      <div key={doc.id} className="p-3 rounded-lg border bg-card">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-sm">Deposit Receipt</p>
                            <p className="text-xs text-muted-foreground">Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => updateDocumentStatus(doc.id, 'verified')}>
                              <CheckCircle className="h-4 w-4 mr-1" /> Verify
                            </Button>
                            <a href={doc.file_data} download>
                              <Button size="sm" variant="outline">
                                <Download className="h-4 w-4" />
                              </Button>
                            </a>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Stage 5: Medical Test */}
              {stageIndex === 5 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Medical Appointment Details</h4>
                  {!medicalAppointment ? (
                    <p className="text-sm text-muted-foreground">No medical appointment scheduled</p>
                  ) : (
                    <div className="p-4 rounded-lg border bg-card">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">City</p>
                          <p className="font-medium">{medicalAppointment.city}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Appointment Date</p>
                          <p className="font-medium">{medicalAppointment.appointment_date}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Medical History</p>
                          <p className="font-medium">{medicalAppointment.medical_history ? 'Yes' : 'No'}</p>
                        </div>
                        {medicalAppointment.medical_history_details && (
                          <div className="col-span-2">
                            <p className="text-xs text-muted-foreground">Details</p>
                            <p className="text-sm">{medicalAppointment.medical_history_details}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Stage 6: Financial Documents */}
              {stageIndex === 6 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Financial Information</h4>
                  {!financialInfo ? (
                    <p className="text-sm text-muted-foreground">No financial information submitted</p>
                  ) : (
                    <div className="p-4 rounded-lg border bg-card">
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Source of Funds</p>
                        <p className="font-medium text-lg">{financialInfo.funding_source === 'personal' ? 'Personal Funds' : financialInfo.funding_source === 'banker_loan' ? 'Education Loan from Banker' : 'Need Help with Education Loan'}</p>
                        <p className="text-xs text-muted-foreground mt-2">Submitted: {new Date(financialInfo.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Stage 7: Interview Prep */}
              {stageIndex === 7 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Interview Preparation Materials</h4>
                  {documents.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No preparation materials uploaded yet</p>
                  ) : (
                    documents.map(material => (
                      <div key={material.id} className="p-3 rounded-lg border bg-card">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">{material.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">{material.description}</p>
                          </div>
                          <a href={material.file_url} download className="text-primary">
                            <Download className="h-4 w-4" />
                          </a>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Stages 8-10: No student submissions */}
              {stageIndex >= 8 && (
                <p className="text-sm text-muted-foreground">
                  No submissions yet for this stage
                </p>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default DetailedStageView;
